import React, { useState } from "react"
import { Box } from "@mui/system"
import PropTypes from "prop-types"
import { Grid, ThemeProvider } from "@mui/material"
import { useBoxSectionRating } from "../../hooks/use-ratings-use-reducer"
import { useRatingEmissaoTable } from "../../hooks/table-emission-hooks/use-last-rating-emission.js"
import ModalDialogPage from "src/components/modal-dialog"
import ParameterTable from "src/components/table/parameter-table"
import AlertMessage from "src/components/alert-message"
import TableGray from "src/components/table/table-gray"
import themeLegacy from "src/utils/theme"
import { usePrecosProcessados } from "../../hooks/table-emission-hooks/use-precos-processados"
import { useRatingDevedorTable } from "../../hooks/table-emission-hooks/use-last-debtor-table"
import { useIndiceLiquidezTable } from "../../hooks/table-emission-hooks/use-last-index-liquidity"
import { useUltimoNegocioTable } from "../../hooks/table-emission-hooks/use-last-business"
import { useUltimoEventoCreditoEmissao } from "../../hooks/table-emission-hooks/use-last-event-credit-emission"
import { useUltimoEventoCreditoDevedor } from "../../hooks/table-emission-hooks/use-last-event-debtor"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import SummarySection from "src/v2/components/SummarySection/summary-section"
import { getSummarySections } from "./constants/summary-sections"
import { getEmissionSummaryModalConfig } from "./configs/emission-modal-config"
import EmissionDetailsModal from "../emission-details-modal/emission-details-modal"
import { getEmissionDetailsModalConfig } from "./configs/get-emission-details-modal-config"
import { mockCharacterData } from "./constants/mock-character-data"
import { mockOfferData } from "./constants/mock-offer-data"

export const EmissionSummarySections = ({
    cetipIsin,
    emissaoService,
    // characterData,
    admin,
}) => {
    // [MOCK] Usa mock quando characterData não for fornecido
    const data = mockCharacterData
    // [ORIGINAL] const cpfCnpj = characterData?.CPF_CNPJ_Devedor
    const cpfCnpj = data?.CPF_CNPJ_Devedor

    const [modalIsVisible, setModalIsVisible] = useState(false)
    const [modalType, setModalType] = useState("")

    const [detailsModalVisible, setDetailsModalVisible] = useState(false)
    const [detailsModalType, setDetailsModalType] = useState("")

    const { state } = useBoxSectionRating(cetipIsin, cpfCnpj, emissaoService)

    // [ORIGINAL] useRatingEmissaoTable(emissaoService, cetipIsin, characterData)
    const { state: stateRating, actions: ratingActions } =
        useRatingEmissaoTable(emissaoService, cetipIsin, data)

    // [ORIGINAL] usePrecosProcessados(emissaoService, cetipIsin, characterData)
    const { state: statePriceProcess, actions: priceProcessActions } =
        usePrecosProcessados(emissaoService, cetipIsin, data)

    // [ORIGINAL] useRatingDevedorTable(emissaoService, cpfCnpj, characterData)
    const { state: stateDebtorEmission, actions: debtorEmissionActions } =
        useRatingDevedorTable(emissaoService, cpfCnpj, data)

    // [ORIGINAL] useIndiceLiquidezTable(emissaoService, cetipIsin, characterData)
    const { state: stateIndexLiquidity, actions: indexLiquidityActions } =
        useIndiceLiquidezTable(emissaoService, cetipIsin, data)

    // [ORIGINAL] useUltimoNegocioTable(emissaoService, cetipIsin, characterData)
    const { state: lastBusinessState, actions: lastBusinessActions } =
        useUltimoNegocioTable(emissaoService, cetipIsin, data)

    // [ORIGINAL] useUltimoEventoCreditoEmissao(emissaoService, cetipIsin, characterData)
    const {
        state: lastEventCreditEmissionState,
        actions: lastEventCreditEmissionActions,
    } = useUltimoEventoCreditoEmissao(emissaoService, cetipIsin, data)

    // [ORIGINAL] useUltimoEventoCreditoDevedor(emissaoService, cpfCnpj, characterData, cetipIsin)
    const {
        state: lastEventCreditDebtorState,
        actions: lastEventCreditDebtorActions,
    } = useUltimoEventoCreditoDevedor(
        emissaoService,
        cpfCnpj,
        data,
        cetipIsin
    )

    const isLoading =
        state.loading ||
        stateRating.loading ||
        statePriceProcess.loading ||
        stateDebtorEmission.loading ||
        stateIndexLiquidity.loading ||
        lastBusinessState.loading ||
        lastEventCreditEmissionState.loading ||
        lastEventCreditDebtorState.loading

    const modalConfig = getEmissionSummaryModalConfig({
        admin,
        stateRating,
        ratingActions,
        stateDebtorEmission,
        debtorEmissionActions,
        lastEventCreditEmissionState,
        lastEventCreditEmissionActions,
        lastEventCreditDebtorState,
        lastEventCreditDebtorActions,
        stateIndexLiquidity,
        indexLiquidityActions,
        lastBusinessState,
        lastBusinessActions,
        statePriceProcess,
        priceProcessActions,
    })

    // [ORIGINAL] getEmissionDetailsModalConfig({ characterData })
    const detailsModalConfig = getEmissionDetailsModalConfig({
        characterData: data,
        offerData: mockOfferData,
    })

    const handleClickSummaryModal = async (value) => {
        if (detailsModalConfig[value]) {
            setDetailsModalType(value)
            setDetailsModalVisible(true)
            return
        }

        if (!modalConfig[value]) return

        await modalConfig[value].loadData()

        setModalType(value)
        setModalIsVisible(true)
    }

    const selectedModalConfig = modalType ? modalConfig[modalType] : null

    const selectedDetailsModalConfig = detailsModalType
        ? detailsModalConfig[detailsModalType]
        : null

    // [ORIGINAL] getSummarySections({ state, characterData, handleClickEmission })
    const ratingSummarySections = getSummarySections({
        state,
        characterData: data,
        handleClickEmission: handleClickSummaryModal,
    })

    return (
        <>
            {isLoading && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}

            <ThemeProvider theme={themeLegacy}>
                <ModalDialogPage
                    open={modalIsVisible}
                    title={selectedModalConfig?.title ?? ""}
                    close={() => {
                        setModalIsVisible(false)
                    }}
                >
                    <>
                        <Grid container direction="column" spacing={2}>
                            <Grid item xs={12} md={12} xl={12}>
                                <TableGray
                                    data={
                                        Array.isArray(
                                            selectedModalConfig?.table
                                        )
                                            ? selectedModalConfig.table
                                            : []
                                    }
                                />

                                <Grid item xs={12} style={{ marginTop: 8 }}>
                                    <AlertMessage
                                        open={
                                            selectedModalConfig?.alert?.open ??
                                            false
                                        }
                                        message={
                                            selectedModalConfig?.alert
                                                ?.message ?? ""
                                        }
                                        type={
                                            selectedModalConfig?.alert?.type ??
                                            undefined
                                        }
                                        close={
                                            selectedModalConfig?.actions
                                                ?.closeAlert
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <br />

                        <ParameterTable
                            rowHeigth={selectedModalConfig?.rowHeight}
                            columns={selectedModalConfig?.columns ?? []}
                            data={selectedModalConfig?.rows ?? []}
                            createRow={
                                selectedModalConfig?.actions?.addRow ??
                                (() => {})
                            }
                            setData={
                                selectedModalConfig?.actions?.addRow ??
                                (() => {})
                            }
                            deleteRow={
                                selectedModalConfig?.actions?.deleteRow ??
                                (() => {})
                            }
                            updateRow={
                                selectedModalConfig?.actions?.updateRow ??
                                (() => {})
                            }
                            loadData={
                                selectedModalConfig?.loadData ??
                                (async () => {})
                            }
                            disableDeleteColumn={
                                !admin || selectedModalConfig?.disableDeleteRow
                            }
                            disableAddRow={!admin}
                            buttonAddVariant="contained"
                            buttonAddTitle={
                                selectedModalConfig?.btnAddTitle ?? ""
                            }
                        />
                    </>
                </ModalDialogPage>
            </ThemeProvider>

            <EmissionDetailsModal
                open={detailsModalVisible}
                onClose={() => {
                    setDetailsModalVisible(false)
                    setDetailsModalType("")
                }}
                title={selectedDetailsModalConfig?.title ?? ""}
                sections={selectedDetailsModalConfig?.sections ?? []}
            />

            <Box>
                <Grid container rowSpacing={3}>
                    {ratingSummarySections.map((section) => (
                        <Grid
                            key={section.title}
                            item
                            {...section.gridProps}
                            sx={{
                                display: "flex",
                                ...(section.sx ?? {}),
                            }}
                        >
                            <SummarySection
                                title={section.title}
                                fields={section.fields}
                                empty={section.empty}
                                emptyText={section.emptyText}
                                onClick={section.onClick}
                                fieldGridProps={section.fieldGridProps}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    )
}

EmissionSummarySections.propTypes = {
    cetipIsin: PropTypes.string,
    emissaoService: PropTypes.any,
    // [ORIGINAL] characterData: PropTypes.any,
    admin: PropTypes.bool,
}